/**
 * Cleanroom implementation of the OpenUI Lang 1.0 streaming parser, top-down
 * reference resolver, incremental statement merger (`mergeOpenUI`), and JSON
 * token-efficiency comparator.
 *
 * Reference specification: https://github.com/thesysdev/openui (MIT License,
 * Copyright (c) 2011-2024 Thesys Inc., inspected at commit c9f93b6e57d0).
 * Built with zero external dependencies and no network or telemetry calls.
 */

export type OpenUIValue =
    | string
    | number
    | boolean
    | null
    | OpenUIValue[]
    | { [key: string]: OpenUIValue }
    | OpenUIComponentNode
    | OpenUIUnresolvedNode;

export interface OpenUIComponentNode {
    __kind: 'component';
    type: string;
    statementId?: string;
    args: OpenUIValue[];
}

export interface OpenUIUnresolvedNode {
    __kind: 'unresolved';
    ref: string;
}

export function isOpenUIComponent(val: OpenUIValue): val is OpenUIComponentNode {
    return Boolean(val && typeof val === 'object' && !Array.isArray(val) && (val as any).__kind === 'component');
}

export function isOpenUIUnresolved(val: OpenUIValue): val is OpenUIUnresolvedNode {
    return Boolean(val && typeof val === 'object' && !Array.isArray(val) && (val as any).__kind === 'unresolved');
}

export interface StatementDescriptor {
    id: string;
    raw: string;
    isState: boolean;
    componentType: string | null;
    reachable: boolean;
}

export interface OpenUIParseResult {
    root: OpenUIComponentNode | null;
    rootId: string | null;
    statements: StatementDescriptor[];
    unresolvedRefs: string[];
    orphanedIds: string[];
    state: Record<string, OpenUIValue>;
    autoClosed: {
        text: string;
        wasIncomplete: boolean;
        openStack: string[];
    };
}

/**
 * Auto-closes unclosed string literals and brackets `(`, `[`, `{` so any
 * streaming prefix of an OpenUI Lang program parses into a valid partial tree.
 */
export function autoClose(input: string): {
    text: string;
    wasIncomplete: boolean;
    openStack: string[];
} {
    const stack: string[] = [];
    let inStr: false | '"' | "'" = false;
    let esc = false;

    for (let i = 0; i < input.length; i++) {
        const c = input[i];
        if (esc) {
            esc = false;
            continue;
        }
        if (c === '\\' && inStr) {
            esc = true;
            continue;
        }
        if (inStr) {
            if (c === inStr) inStr = false;
            continue;
        }
        if (c === '"' || c === "'") {
            inStr = c;
            continue;
        }
        if (c === '(' || c === '[' || c === '{') stack.push(c);
        else if (c === ')' && stack[stack.length - 1] === '(') stack.pop();
        else if (c === ']' && stack[stack.length - 1] === '[') stack.pop();
        else if (c === '}' && stack[stack.length - 1] === '{') stack.pop();
    }

    const wasIncomplete = Boolean(inStr) || stack.length > 0;
    if (!wasIncomplete) {
        return { text: input, wasIncomplete: false, openStack: [] };
    }

    let out = input;
    const openStack = [...stack];
    if (inStr) {
        if (esc) out += '\\';
        out += inStr;
        openStack.push(inStr);
    }
    for (let j = stack.length - 1; j >= 0; j--) {
        out += stack[j] === '(' ? ')' : stack[j] === '[' ? ']' : '}';
    }
    return { text: out, wasIncomplete: true, openStack };
}

/**
 * Strip markdown fences and `]]>openui:` protocol envelope lines.
 */
export function cleanOpenUISource(raw: string): string {
    return raw
        .replace(/^```(?:openui(?:-lang)?)\s*\r?\n?/i, '')
        .replace(/\r?\n?```\s*$/i, '')
        .split(/\r?\n/)
        .filter(line => !line.trim().startsWith(']]>openui:'))
        .join('\n');
}

/**
 * Splits top-level `identifier = expression` statements separated by newlines
 * at bracket depth 0.
 */
export function splitStatements(source: string): { id: string; exprText: string; raw: string }[] {
    const cleaned = cleanOpenUISource(source);
    const lines: string[] = [];
    let depth = 0;
    let inStr: false | '"' | "'" = false;
    let esc = false;
    let start = 0;

    for (let i = 0; i < cleaned.length; i++) {
        const c = cleaned[i];
        if (esc) {
            esc = false;
            continue;
        }
        if (c === '\\' && inStr) {
            esc = true;
            continue;
        }
        if (inStr) {
            if (c === inStr) inStr = false;
            continue;
        }
        if (c === '"' || c === "'") {
            inStr = c;
            continue;
        }
        if (c === '(' || c === '[' || c === '{') depth++;
        else if (c === ')' || c === ']' || c === '}') depth = Math.max(0, depth - 1);
        else if (c === '\n' && depth === 0) {
            const chunk = cleaned.slice(start, i).trim();
            if (chunk && !chunk.startsWith('//') && !chunk.startsWith('#')) lines.push(chunk);
            start = i + 1;
        }
    }
    const tail = cleaned.slice(start).trim();
    if (tail && !tail.startsWith('//') && !tail.startsWith('#')) lines.push(tail);

    const out: { id: string; exprText: string; raw: string }[] = [];
    for (const raw of lines) {
        const match = raw.match(/^(\$?[A-Za-z_][A-Za-z0-9_]*)\s*=\s*([\s\S]+)$/);
        if (!match) continue;
        out.push({ id: match[1], exprText: match[2].trim(), raw });
    }
    return out;
}

type ExprAST =
    | { k: 'lit'; v: string | number | boolean | null }
    | { k: 'ref'; name: string }
    | { k: 'arr'; items: ExprAST[] }
    | { k: 'obj'; entries: [string, ExprAST][] }
    | { k: 'call'; name: string; args: ExprAST[] };

function splitTopLevelCommas(text: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let inStr: false | '"' | "'" = false;
    let esc = false;
    let start = 0;

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (esc) {
            esc = false;
            continue;
        }
        if (c === '\\' && inStr) {
            esc = true;
            continue;
        }
        if (inStr) {
            if (c === inStr) inStr = false;
            continue;
        }
        if (c === '"' || c === "'") {
            inStr = c;
            continue;
        }
        if (c === '(' || c === '[' || c === '{') depth++;
        else if (c === ')' || c === ']' || c === '}') depth = Math.max(0, depth - 1);
        else if (c === ',' && depth === 0) {
            const seg = text.slice(start, i).trim();
            if (seg) parts.push(seg);
            start = i + 1;
        }
    }
    const last = text.slice(start).trim();
    if (last) parts.push(last);
    return parts;
}

function parseExpr(raw: string): ExprAST {
    const s = raw.trim();
    if (!s) return { k: 'lit', v: null };
    if (s === 'true') return { k: 'lit', v: true };
    if (s === 'false') return { k: 'lit', v: false };
    if (s === 'null') return { k: 'lit', v: null };
    if (/^-?\d+(?:\.\d+)?$/.test(s)) return { k: 'lit', v: Number(s) };

    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
        const inner = s.slice(1, -1).replace(/\\(["'\\])/g, '$1');
        return { k: 'lit', v: inner };
    }

    if (s.startsWith('[') && s.endsWith(']')) {
        const body = s.slice(1, -1).trim();
        if (!body) return { k: 'arr', items: [] };
        return { k: 'arr', items: splitTopLevelCommas(body).map(parseExpr) };
    }

    if (s.startsWith('{') && s.endsWith('}')) {
        const body = s.slice(1, -1).trim();
        if (!body) return { k: 'obj', entries: [] };
        const entries: [string, ExprAST][] = [];
        for (const pair of splitTopLevelCommas(body)) {
            const colon = pair.indexOf(':');
            if (colon === -1) continue;
            const key = pair.slice(0, colon).trim().replace(/^["']|["']$/g, '');
            const val = parseExpr(pair.slice(colon + 1));
            entries.push([key, val]);
        }
        return { k: 'obj', entries };
    }

    const callMatch = s.match(/^(@?[A-Za-z_][A-Za-z0-9_]*)\(([\s\S]*)\)$/);
    if (callMatch) {
        const name = callMatch[1];
        const argBody = callMatch[2].trim();
        const args = argBody ? splitTopLevelCommas(argBody).map(parseExpr) : [];
        return { k: 'call', name, args };
    }

    if (/^\$?[A-Za-z_][A-Za-z0-9_]*$/.test(s)) {
        return { k: 'ref', name: s };
    }

    return { k: 'lit', v: s };
}

function collectAstRefs(node: ExprAST, out: Set<string>): void {
    if (node.k === 'ref') {
        out.add(node.name);
        return;
    }
    if (node.k === 'arr') {
        node.items.forEach(item => collectAstRefs(item, out));
        return;
    }
    if (node.k === 'obj') {
        node.entries.forEach(([, v]) => collectAstRefs(v, out));
        return;
    }
    if (node.k === 'call') {
        node.args.forEach(arg => collectAstRefs(arg, out));
    }
}

function evalBuiltin(name: string, args: OpenUIValue[]): OpenUIValue {
    const first = args[0];
    const nums = Array.isArray(first)
        ? first.filter((x): x is number => typeof x === 'number')
        : args.filter((x): x is number => typeof x === 'number');

    switch (name) {
        case '@Count':
            return Array.isArray(first) ? first.length : args.length;
        case '@Sum':
            return nums.reduce((a, b) => a + b, 0);
        case '@Avg':
            return nums.length ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2)) : 0;
        case '@Min':
            return nums.length ? Math.min(...nums) : 0;
        case '@Max':
            return nums.length ? Math.max(...nums) : 0;
        case '@Round': {
            const val = typeof args[0] === 'number' ? args[0] : 0;
            const digits = typeof args[1] === 'number' ? args[1] : 0;
            const factor = Math.pow(10, digits);
            return Math.round(val * factor) / factor;
        }
        default:
            return { __kind: 'component', type: name, args };
    }
}

/**
 * Parses a complete or partial (streaming) OpenUI Lang program, auto-closing
 * open strings/brackets, resolving hoisted references from `root`, and
 * tracking unresolved forward references and orphaned statements.
 */
export function parseOpenUI(
    input: string,
    stateOverrides: Record<string, OpenUIValue> = {},
): OpenUIParseResult {
    const autoClosed = autoClose(cleanOpenUISource(input));
    const rawStmts = splitStatements(autoClosed.text);

    const astById = new Map<string, { ast: ExprAST; raw: string }>();
    for (const s of rawStmts) {
        astById.set(s.id, { ast: parseExpr(s.exprText), raw: s.raw });
    }

    const state: Record<string, OpenUIValue> = {};
    astById.forEach((entry, id) => {
        if (id.startsWith('$') && entry.ast.k === 'lit') {
            state[id] = entry.ast.v;
        }
    });
    Object.assign(state, stateOverrides);

    let rootId: string | null = astById.has('root') ? 'root' : null;
    if (!rootId) {
        astById.forEach((entry, id) => {
            if (!rootId && !id.startsWith('$') && entry.ast.k === 'call' && !entry.ast.name.startsWith('@')) {
                rootId = id;
            }
        });
    }

    const unresolvedSet = new Set<string>();
    const reachableIds = new Set<string>();

    const evaluateNode = (ast: ExprAST, visiting: Set<string>, stmtId?: string): OpenUIValue => {
        switch (ast.k) {
            case 'lit':
                return ast.v;
            case 'arr':
                return ast.items.map(item => evaluateNode(item, visiting));
            case 'obj': {
                const out: { [key: string]: OpenUIValue } = {};
                for (const [k, v] of ast.entries) {
                    out[k] = evaluateNode(v, visiting);
                }
                return out;
            }
            case 'ref': {
                if (ast.name.startsWith('$')) {
                    return ast.name in state ? state[ast.name] : null;
                }
                if (visiting.has(ast.name)) {
                    return { __kind: 'unresolved', ref: ast.name };
                }
                const target = astById.get(ast.name);
                if (!target) {
                    unresolvedSet.add(ast.name);
                    return { __kind: 'unresolved', ref: ast.name };
                }
                reachableIds.add(ast.name);
                const nextVisiting = new Set(visiting);
                nextVisiting.add(ast.name);
                return evaluateNode(target.ast, nextVisiting, ast.name);
            }
            case 'call': {
                const evaluatedArgs = ast.args.map(arg => evaluateNode(arg, visiting));
                if (ast.name.startsWith('@')) {
                    return evalBuiltin(ast.name, evaluatedArgs);
                }
                return {
                    __kind: 'component',
                    type: ast.name,
                    statementId: stmtId,
                    args: evaluatedArgs,
                };
            }
        }
    };

    let rootNode: OpenUIComponentNode | null = null;
    if (rootId && astById.has(rootId)) {
        reachableIds.add(rootId);
        const evaluated = evaluateNode(astById.get(rootId)!.ast, new Set([rootId]), rootId);
        if (isOpenUIComponent(evaluated)) {
            rootNode = evaluated;
        }
    }

    const statements: StatementDescriptor[] = [];
    const orphanedIds: string[] = [];

    astById.forEach(({ ast, raw }, id) => {
        const isState = id.startsWith('$');
        const reachable = isState || reachableIds.has(id);
        if (!reachable) orphanedIds.push(id);
        statements.push({
            id,
            raw,
            isState,
            componentType: ast.k === 'call' && !ast.name.startsWith('@') ? ast.name : null,
            reachable,
        });
    });

    return {
        root: rootNode,
        rootId,
        statements,
        unresolvedRefs: Array.from(unresolvedSet),
        orphanedIds,
        state,
        autoClosed,
    };
}

/**
 * Merges an incremental OpenUI Lang patch into an existing program by statement
 * name, removing tombstoned (`id = null`) and unreachable statements.
 */
export function mergeOpenUI(baseSource: string, patchSource: string): {
    mergedSource: string;
    updatedIds: string[];
    removedIds: string[];
} {
    const baseStmts = splitStatements(baseSource);
    const patchStmts = splitStatements(patchSource);

    const map = new Map<string, { exprText: string; raw: string }>();
    for (const s of baseStmts) {
        map.set(s.id, { exprText: s.exprText, raw: s.raw });
    }

    const updatedIds: string[] = [];
    const removedIds: string[] = [];

    for (const p of patchStmts) {
        if (p.exprText === 'null') {
            if (map.delete(p.id)) removedIds.push(p.id);
        } else {
            map.set(p.id, { exprText: p.exprText, raw: `${p.id} = ${p.exprText}` });
            updatedIds.push(p.id);
        }
    }

    // Garbage-collect statements no longer reachable from root
    const asts = new Map<string, ExprAST>();
    map.forEach((val, id) => {
        asts.set(id, parseExpr(val.exprText));
    });

    const reachable = new Set<string>();
    const walkReachable = (id: string) => {
        if (reachable.has(id)) return;
        reachable.add(id);
        const ast = asts.get(id);
        if (!ast) return;
        const refs = new Set<string>();
        collectAstRefs(ast, refs);
        refs.forEach(ref => {
            if (asts.has(ref)) walkReachable(ref);
        });
    };

    if (asts.has('root')) {
        walkReachable('root');
    } else {
        const firstId = Array.from(asts.keys()).find(id => !id.startsWith('$'));
        if (firstId) walkReachable(firstId);
    }

    const keptLines: string[] = [];
    map.forEach((val, id) => {
        if (id.startsWith('$') || reachable.has(id)) {
            keptLines.push(val.raw);
        } else {
            removedIds.push(id);
        }
    });

    return {
        mergedSource: keptLines.join('\n'),
        updatedIds,
        removedIds,
    };
}

/**
 * Converts a materialized OpenUI node into the verbose nested JSON UI format
 * that standard JSON-mode LLM UI generators emit, to measure token efficiency.
 */
export function toEquivalentJson(node: OpenUIValue): unknown {
    if (node === null || typeof node !== 'object') return node;
    if (Array.isArray(node)) return node.map(toEquivalentJson);
    if (isOpenUIUnresolved(node)) {
        return { $unresolvedRef: node.ref };
    }
    if (isOpenUIComponent(node)) {
        return {
            component: node.type,
            id: node.statementId ?? undefined,
            props: {
                childrenAndArgs: node.args.map(toEquivalentJson),
            },
        };
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) {
        out[k] = toEquivalentJson(v);
    }
    return out;
}

export function estimateTokens(text: string): number {
    if (!text.trim()) return 0;
    // Standard ~3.6 chars/token BPE heuristic for structured punctuation + identifiers
    const tokens = text.trim().match(/[A-Za-z0-9_$@]+|[^\sA-Za-z0-9_$@]/g);
    return tokens ? tokens.length : Math.ceil(text.length / 4);
}

export function comparePayloadEfficiency(openuiSource: string): {
    openuiTokens: number;
    jsonTokens: number;
    reductionPct: number;
    jsonString: string;
} {
    const parsed = parseOpenUI(openuiSource);
    const jsonTree = parsed.root ? toEquivalentJson(parsed.root) : {};
    const jsonString = JSON.stringify(jsonTree, null, 2);
    const openuiTokens = estimateTokens(cleanOpenUISource(openuiSource));
    const jsonTokens = estimateTokens(jsonString);
    const reductionPct = jsonTokens > 0
        ? Math.round(((jsonTokens - openuiTokens) / jsonTokens) * 100)
        : 0;
    return { openuiTokens, jsonTokens, reductionPct, jsonString };
}
